import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {StorageHelper} from '../utils/storageHelper';
import {CryptoHelper} from '../utils/cryptoHelper';
import {
  CompleteSetupRequestDto,
  LoginRequestDto, LoginResponseDto,
  RegisterUserRequestDto,
  SetupRequirement,
  UsersClient
} from '../api/api-client';

// User interface to match what components expect
interface User {
  id: number;
  email: string;
}

// Define the auth context shape
export interface AuthContextType {
  isAuthenticated: boolean;
  userEmail: string | null;
  user: User | null; // Add user property
  masterKey: CryptoKey | null;
  encrytedMasterKey: string | null;
  publicKey: CryptoKey | null;
  setupNeeded: SetupRequirement;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (returnTo?: string) => void;
  register: (email: string, password: string) => Promise<void>;
  completeSetup: (passphrase: string, rememberPassphrase?: boolean) => Promise<LoginResponseDto>;
  logout: () => void;
  isInitialized: boolean;
  refreshUser: () => Promise<void>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  userEmail: null,
  user: null,
  masterKey: null,
  encrytedMasterKey: null,
  publicKey: null,
  setupNeeded: SetupRequirement.None,
  login: async () => {},
  loginWithGoogle: () => {},
  register: async () => {},
  completeSetup: async () => ({} as LoginResponseDto),
  logout: () => {},
  isInitialized: false,
  refreshUser: () => Promise.resolve(),
});

// Define props for the provider component
interface AuthProviderProps {
  children: ReactNode;
  apiBaseUrl?: string;
}

// Create the provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children, apiBaseUrl = '' }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);  // Add user state
  const [masterKey, setMasterKey] = useState<CryptoKey | null>(null);
  const [encrytedMasterKey, setEncrytedMasterKey] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<CryptoKey | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [setupNeeded, setSetupNeeded] = useState<SetupRequirement>(SetupRequirement.None);

  // Create the API client
  const usersClient = new UsersClient(apiBaseUrl);

  // Function to load user data from the server using the current-user endpoint
  const loadUserFromServer = useCallback(async () => {
    const userData = await usersClient.getCurrentUser();

    if (!userData.userId || !userData.email) {
      throw new Error('User incomplete');
    }

    // Update state
    setIsAuthenticated(true);
    setUserEmail(userData.email);
    setUser({ id: userData.userId, email: userData.email });
    setSetupNeeded(userData.setupNeeded ?? SetupRequirement.None);
    setEncrytedMasterKey(userData.encryptedMasterKey ?? null);

    // If user has an encrypted master key, attempt to load the master key using 
    // the stored passphrase if available
    if (userData.encryptedMasterKey) {
      const storedPassphraseKey = StorageHelper.getPassphrase();
      
      if (storedPassphraseKey) {
        try {
          const passphraseKey = await CryptoHelper.importStoredPassphraseKey(storedPassphraseKey);
          const privateKey = await CryptoHelper.decryptMasterKey(userData.encryptedMasterKey, passphraseKey);
          setMasterKey(privateKey);
        } catch (error) {
          console.error('Failed to decrypt master key with stored passphrase:', error);
          // Clear invalid key and prompt for passphrase
          StorageHelper.clearPassphrase();
          setMasterKey(null);
        }
      }
    }

    // Load public key if available
    if (userData.publicKey) {
      const importedPublicKey = await CryptoHelper.importPublicKey(userData.publicKey);
      setPublicKey(importedPublicKey);
    }
    return userData;
  }, []);

  // Initialize auth state from server
  useEffect(() => {
    const init = async () => {
      console.log('Initializing auth state...');
      
      // First try to get the user from the server
      await loadUserFromServer();
      
      setIsInitialized(true);
      
      // Check for any successful auth callback
      const urlParams = new URLSearchParams(window.location.search);
      console.log('Checking auth params:', Object.fromEntries(urlParams.entries()));
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      console.log('Hash params:', Object.fromEntries(hashParams.entries()));
      
      // Check both query string and hash for auth params
      const isAuthSuccess = urlParams.get('auth') === 'success' || hashParams.get('auth') === 'success';
      console.log('Auth success?', isAuthSuccess);
      
      if (isAuthSuccess) {
        console.log('Auth success detected in URL, verifying with server...');
        // Call the generic auth verification endpoint
        await handleAuthCallback();
      }
    };
    
    init();
  }, []);

  // Handle any authentication callback
  const handleAuthCallback = async () => {
    try {
      console.log('Making API call to verify authentication');
      
      const client = new UsersClient();
      const result = await client.verifyAuth();
      console.log('Received auth data from API:', result);
      
      // Update state
      setIsAuthenticated(true);
      setUserEmail(result.email ?? null);
      setUser({ id: result.userId ?? 0, email: result.email ?? "" });
      setEncrytedMasterKey(result.encryptedMasterKey ?? null);

      // If user has completed setup and we have an encrypted master key,
      // attempt to load the master key using the stored passphrase if available
      if (result.encryptedMasterKey) {
        const storedPassphraseKey = StorageHelper.getPassphrase();
        
        if (storedPassphraseKey) {
          try {
            const passphraseKey = await CryptoHelper.importStoredPassphraseKey(storedPassphraseKey);
            const privateKey = await CryptoHelper.decryptMasterKey(result.encryptedMasterKey, passphraseKey);
            setMasterKey(privateKey);
          } catch (error) {
            console.error('Failed to decrypt master key with stored passphrase:', error);
            // Clear invalid key and prompt for passphrase
            StorageHelper.clearPassphrase();
            setMasterKey(null);
          }
        }
      }
      
      // Load public key if available
      if (result.publicKey) {
        const importedPublicKey = await CryptoHelper.importPublicKey(result.publicKey);
        setPublicKey(importedPublicKey);
      }
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, '/');
      console.log('Auth completed successfully via API!');
      
      return true;
    } catch (error) {
      console.error('Auth callback API verification failed:', error);
      // Don't redirect to login on error - just return false
      return false;
    }
  };
  
  // Login with password
  const login = async (email: string, password: string) => {
    const loginDto: LoginRequestDto = {
      email,
      password
    };
    
    const result = await usersClient.login(loginDto);
    
    if (!result.email) throw new Error('Failed to login - email missing');
    if (!result.userId) throw new Error('Failed to login - userId missing');
    if (!result.token) throw new Error('Failed to login - token missing');
    
    // Save token and user data
    StorageHelper.setToken(result.token);
    
    // Decrypt the master key using the stored passphrase if setup is complete
    if (result.encryptedMasterKey) {
      // See if we have a stored passphrase
      const storedPassphraseKey = StorageHelper.getPassphrase();
      if (storedPassphraseKey) {
        // Use stored passphrase to decrypt
        try {
          const passphraseKey = await CryptoHelper.importStoredPassphraseKey(storedPassphraseKey);
          const privateKey = await CryptoHelper.decryptMasterKey(result.encryptedMasterKey, passphraseKey);
          setMasterKey(privateKey);
        } catch (error) {
          console.error('Failed to decrypt master key with stored passphrase:', error);
          // Clear invalid key and prompt for passphrase
          StorageHelper.clearPassphrase();
          setMasterKey(null);
        }
      }
    }
    
    // Update state
    setIsAuthenticated(true);
    setEncrytedMasterKey(result.encryptedMasterKey ?? null);
    setUserEmail(result.email);
    setSetupNeeded(result.setupNeeded ?? SetupRequirement.None);
    setUser({ id: result.userId, email: result.email });
  };
  
  // Initiate Google Login
  const loginWithGoogle = (returnTo?: string) => {
    // Redirect to a user-friendly login endpoint with optional return URL
    const url = returnTo ? `/login/google?returnTo=${encodeURIComponent(returnTo)}` : '/login/google';
    window.location.href = url;
  };

  // Register with password
  const register = async (email: string, password: string) => {
    // Generate a new key pair
    const keyPair = await CryptoHelper.generateKeyPair();
    
    // Save public key for future use
    setPublicKey(keyPair.publicKey);
    
    // Encrypt the private key with the password
    const encryptedMasterKey = await CryptoHelper.encryptMasterKey(keyPair.privateKey, password);
    
    // Export the public key
    const publicKeyString = await CryptoHelper.exportPublicKey(keyPair.publicKey);
    
    const registerDto: RegisterUserRequestDto = {
      email,
      password,
      encryptedMasterKey,
      publicKey: publicKeyString
    };
    
    // Call the API to register
    await usersClient.register(registerDto);
    
    // Login after successful registration
    await login(email, password);
  };
  
  // Complete setup for Google users (set passphrase and keys)
  const completeSetup = async (passphrase: string, rememberPassphrase: boolean = false) => {
    console.log('Starting setup process...');
    
    if (!isAuthenticated) {
      console.error('Setup failed: User not authenticated or missing user ID');
      throw new Error('User must be authenticated to complete setup');
    }
    
    console.log('Generating key pair...');
    
    // Generate a new key pair
    const keyPair = await CryptoHelper.generateKeyPair();
    console.log('Key pair generated successfully');
    
    // Save keys
    setMasterKey(keyPair.privateKey);
    setPublicKey(keyPair.publicKey);
    
    console.log('Encrypting master key with passphrase...');
    // Encrypt the private key with the passphrase
    const encryptedMasterKey = await CryptoHelper.encryptMasterKey(keyPair.privateKey, passphrase);
    
    console.log('Exporting public key...');
    // Export the public key
    const publicKeyString = await CryptoHelper.exportPublicKey(keyPair.publicKey);
    
    // Create the complete setup DTO
    const setupDto: CompleteSetupRequestDto = {
      encryptedMasterKey,
      publicKey: publicKeyString
    };
    
    console.log('Calling API to complete setup...');
    
    const client = new UsersClient();
    var result = await client.completeSetup(setupDto);
    console.log('Setup completed successfully on server:', result);
    
    // Save the new token and derived key
    StorageHelper.setToken(result.token ?? "");
    
    // Derive and store the passphrase key (secure, non-reversible)
    const data = CryptoHelper.base64ToArrayBuffer(encryptedMasterKey);
    const salt = new Uint8Array(data.slice(0, 16));
    const passphraseKey = await CryptoHelper.deriveKeyFromPassword(passphrase, salt);
    const exportedKey = await window.crypto.subtle.exportKey("raw", passphraseKey);
    const passphraseKeyBase64 = CryptoHelper.arrayBufferToBase64(exportedKey);
    StorageHelper.setPassphrase(passphraseKeyBase64, rememberPassphrase);

    loadUserFromServer();
    
    return result;
  };

  // Logout function
  const logout = async () => {
    // Call the server-side logout endpoint to clear the auth cookie
    await fetch('/login/logout', {
      method: 'GET',
      credentials: 'include'
    });
    
    // Clear local storage data
    StorageHelper.clearAuthData();
    
    // Reset state
    setIsAuthenticated(false);
    setUserEmail(null);
    setUser(null);
    setMasterKey(null);
    setPublicKey(null);
    
    // Wait a brief moment to ensure everything is cleared
    setTimeout(() => {
      // Redirect to login page
      window.location.href = '/login';
    }, 100);
  };

  // Create the context value
  const contextValue: AuthContextType = {
    isAuthenticated,
    userEmail,
    user,  // Add user to context value
    masterKey,
    encrytedMasterKey,
    publicKey,
    login,
    loginWithGoogle,
    register,
    completeSetup,
    logout,
    isInitialized,
    setupNeeded,
    refreshUser: async () => {
      await loadUserFromServer();
    }
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);